/**
 * Insurance-state CTAs (migration 2026-10-09-410 rev 2; owner 2026-10-09, two decisions).
 *
 * The DATABASE decides an event's variant from its VENUE state and hands the browser `base_variant` (render snapshot /
 * event_cta_base_variant):
 *   'insurance' = venue in an insurance state: subscription links carry NO discountvalue, and the flyer uses the
 *                 insurance artwork with no amount ("insurance events must not advertise $20");
 *   'classroom' = a gun show or range night there: the same, and the link (not member-signup) goes to /classroom/;
 *   'standard'  = everything else, unchanged.
 * Pure: no imports, so the URL builder, the flyer service and their tests can all use it.
 */
export type CTABaseVariant = "standard" | "insurance" | "classroom";
export type CTALinkVariant = CTABaseVariant;

/** Mirrors the database's _eib_cta_link_variant: the form THIS CTA's link takes. */
export function ctaLinkVariant(
  cta: { cta_type: string; use_member_signup?: boolean | null },
  baseVariant: CTABaseVariant | string | null | undefined,
): CTALinkVariant {
  if (cta.cta_type !== "subscription") {
    return "standard";
  }
  if (baseVariant === "classroom") {
    return cta.use_member_signup === true ? "insurance" : "classroom";
  }
  return baseVariant === "insurance" ? "insurance" : "standard";
}

/** The link goes to the classroom page (gun show / range night in an insurance state, not member-signup). */
export function ctaUsesClassroomLink(
  cta: { cta_type: string; use_member_signup?: boolean | null },
  baseVariant: CTABaseVariant | string | null | undefined,
): boolean {
  return ctaLinkVariant(cta, baseVariant) === "classroom";
}

/** No discount amount anywhere: not in the link, not on the flyer (any insurance-state subscription CTA). */
export function ctaOmitsDiscount(
  cta: { cta_type: string; use_member_signup?: boolean | null },
  baseVariant: CTABaseVariant | string | null | undefined,
): boolean {
  return ctaLinkVariant(cta, baseVariant) !== "standard";
}

/** Normalise what the server returned (unknown values = standard, as before migration 410). */
export function toBaseVariant(v: unknown): CTABaseVariant {
  return v === "classroom" || v === "insurance" ? v : "standard";
}

/** Shown wherever a voucher would otherwise be offered on an insurance-state event's subscription CTA. */
export const INSURANCE_NO_VOUCHER_NOTE =
  "No voucher here: this event\u2019s venue is in a state where U.S. LawShield offers no instant credit for this kind of event (an insurance state with no voucher, or a state with no Event in a Box offer), so its subscription CTAs carry no voucher and the link and flyer show no amount. Use \u201cNone\u201d \u2014 tracking codes and rep attribution still work.";
